/**
 * The component editor. ONE of them, for every surface that edits an XBlock.
 *
 * The unit page and the library page both open this, with nothing but a key and a name, and
 * an author cannot tell which surface they are on. That is not tidiness for its own sake:
 * before this existed, clicking a component in a course opened the platform's real editor
 * while clicking one in a library opened a raw OLX textarea, and the second is not a
 * different design decision — it is the same job done worse.
 *
 * What it does, in order:
 *
 *   1. ASKS THE SERVER which editor this block gets. Verawood has deleted the
 *      server-rendered editor from `html` and `problem` while keeping it for video, ORA,
 *      LTI, poll, survey and the rest, and nothing in the platform declares which. The
 *      probe measures the block itself, per store — see `editors.py`.
 *   2. MINTS A CMS SESSION, before any iframe exists. Both embeds authenticate with a
 *      Django session cookie, which the JWT that authenticates everything else here is
 *      invisible to. An iframe rendered first is redirected to a login page and STAYS
 *      there, because nothing reloads it when the session appears a moment later.
 *   3. EMBEDS the platform's editor, or falls back to ours over the block's source.
 *
 * The two embeds speak different protocols, and this component understands both, because
 * which one it is talking to is not something the caller should have to know:
 *
 *   Studio's editor page (course components) posts `saveEditedXBlockData` and
 *   `closeXBlockEditorModal` — names read off `cms/static/js/views/modals/edit_xblock.js`.
 *
 *   The v2 iframe embed (library components) posts `{type: 'xblock-event', eventName}` with
 *   `save`/`cancel`/`error`, plus `plugin.resize` — see `common/templates/xblock_v2/
 *   xblock_iframe.html`, which is also what the official authoring MFE listens for in
 *   `src/editors/AdvancedEditor.tsx`.
 *
 * There is always a "Done editing" button regardless. An author must never be trapped in a
 * dialog because a message name changed in an upgrade, or because a third-party block never
 * sends one.
 *
 * AND IT CHECKS THAT THE EMBEDDED EDITOR IS ALIVE. Measured on k8s dev: a library
 * component's editor loaded with all fourteen of its own stylesheets and scripts blocked as
 * mixed content, so the XBlock runtime never initialised and upstream's Save — an anchor
 * with no handler — did nothing at all. Zero requests, no error, and the author's new name
 * gone after a reload. The deployment setting is the fix for that (see
 * docs/authoring-embed-https.md); the guard here is the fix for the SILENCE, and it stays
 * afterwards, because a 404, a CSP or one tenant left on a stale `http://` origin
 * reproduces it exactly. See `shared/lib/embed.ts` for what is measured and why one of the
 * two signals is corroborating rather than decisive.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Banner, Button, Dialog, Spinner } from '../../shared/ui';
import { editorEmbedUrl, getEditor } from '../../shared/lib/editor';
import { blockedOrigins, inspectEmbed, type EmbedProbe } from '../../shared/lib/embed';
import { isEditorDoneMessage, isResizeMessage, startStudioSession } from '../../shared/lib/unit';
import { DataEditor } from './DataEditor';
import QuestionEditor from './QuestionEditor';
import VideoEditor from './VideoEditor';
import type { QuestionRefusal } from '../../shared/lib/editor';
import { headlineFor, isInstallWide } from '../../shared/lib/question-refusals';
import './component-editor.css';
import './question-editor.css';

/** Height bounds for a resize message, so a bad number cannot collapse the editor. */
const MIN_FRAME = 360;
const MAX_FRAME = 4000;

/**
 * How long an embed may say nothing before we look at whether its JS ever ran.
 *
 * A healthy embed posts `plugin.resize` as soon as its runtime starts. Six seconds is
 * generous against the measured worst case on this deployment — the embed's own CDN burst
 * (108 MathJax requests) spans 4.8 s — so an editor that is merely slow is never accused,
 * and the accusation withdraws itself the moment a message arrives.
 */
const EMBED_SILENCE_MS = 6000;

/**
 * The v2 embed's own message. `save` with `{state: 'end'}` means the block's handler
 * returned success, which is the only moment it is safe to close on the author's behalf.
 */
interface XBlockEvent {
  type?: string;
  eventName?: string;
  data?: { state?: string };
}

function xblockEvent(data: unknown): XBlockEvent | null {
  if (typeof data !== 'object' || data === null) return null;
  const message = data as XBlockEvent;
  return message.type === 'xblock-event' && typeof message.eventName === 'string'
    ? message
    : null;
}

/** What is wrong with the embed, in the words the author needs. `null` = nothing is. */
function embedFault(probe: EmbedProbe | null, silent: boolean): 'assets' | 'runtime' | null {
  if (!probe) return null;
  // Deterministic: the browser refuses these by specification.
  if (probe.blocked.length > 0) return 'assets';
  // Corroborated only. Silence alone is not a fault, and missing globals alone could be a
  // slow load; together, after six seconds, the runtime is not coming.
  if (silent && probe.missingRuntime.length > 0) return 'runtime';
  return null;
}

/**
 * Why this question opened as XML instead of as a form.
 *
 * SURFACED, NEVER SWALLOWED. Getting the raw editor is not a regression — it is what every
 * question had before this feature existed — but getting it with no explanation teaches an
 * author that the product is arbitrary. Two sentences, with different jobs: the headline says
 * what class of thing stopped the form, in an author's vocabulary; the server's `detail` names
 * the actual construct it found in this particular question.
 *
 * The last line differs by whether the refusal is about this question or about the whole
 * install, because telling someone to look at this question when the form is switched off
 * everywhere sends them hunting for a fault that is not there.
 */
function QuestionRefusalNote({ refusal }: { refusal: QuestionRefusal }) {
  return (
    <Banner tone="info">
      <strong>{headlineFor(refusal.reason)}</strong> {refusal.detail}{' '}
      {isInstallWide(refusal.reason)
        ? 'Every question here is edited as XML for now.'
        : 'You can edit it as XML below, exactly as before.'}
    </Banner>
  );
}

export function ComponentEditor({
  blockKey,
  blockName,
  publishTarget,
  readOnly = false,
  onClose,
  onSaved,
}: {
  blockKey: string;
  blockName: string;
  /**
   * What the author has to publish for this edit to reach a learner. Both surfaces write a
   * draft, but a unit publishes on its own and a library publishes as a whole, so the
   * sentence after a save differs — and it is the one sentence an author who has just
   * pressed Save actually needs.
   */
  publishTarget: 'unit' | 'library';
  /** A library the author may read but not change. Never embeds an editor they cannot use. */
  readOnly?: boolean;
  onClose: () => void;
  /** Called after a successful save, so the caller can refetch whatever went stale. */
  onSaved: () => void;
}) {
  const [frameHeight, setFrameHeight] = useState<number | null>(null);

  /**
   * The author asked to see this question's XML instead of the form.
   *
   * A PREFERENCE, NOT A FALLBACK, which is why it is state here and not something the
   * capability probe decides. A question the form cannot represent never has a form to
   * begin with — the server answers kind `'olx'` with a refusal — so this flag only ever
   * carries the case where the form WAS offered and the author chose the source anyway: to
   * copy a question, to check what the form wrote, or to use a construct the form has no
   * control for.
   *
   * It is deliberately one-way per opening of the dialog and never persisted. Remembering
   * it across components would mean an author who once looked at some XML silently stops
   * being offered the form, and losing a preference is a much smaller harm than a screen
   * that quietly degrades for reasons the author cannot see. Toggling it remounts whichever
   * editor comes back, so each one re-reads the block rather than showing what it held
   * before the other one saved.
   */
  const [showSource, setShowSource] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const queryClient = useQueryClient();

  /** Set once the iframe document has loaded, which is when it is worth inspecting. */
  const [probe, setProbe] = useState<EmbedProbe | null>(null);
  const [loaded, setLoaded] = useState(false);
  /** Any message at all from the frame proves its JS is running. */
  const [spoke, setSpoke] = useState(false);
  const [silent, setSilent] = useState(false);

  /**
   * Which editor this component gets.
   *
   * Cached briefly per block: the answer is a property of the block type, and an author
   * editing three problems in a row should not wait for three identical probes.
   */
  const capability = useQuery({
    queryKey: ['editor-capability', blockKey],
    queryFn: () => getEditor(blockKey),
    staleTime: 5 * 60_000,
  });

  /**
   * Fetched as soon as the dialog opens rather than after the probe answers, so the session
   * is already warm by the time we know whether an iframe is needed. Ten minutes, not
   * forever: a cached success outliving the real Django session would put us straight back
   * to showing a login page inside the dialog.
   */
  const session = useQuery({
    queryKey: ['studio-session'],
    queryFn: startStudioSession,
    enabled: !readOnly,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  const embedded = capability.data?.kind === 'studio' && !readOnly;

  /**
   * A save happened, wherever it came from.
   *
   * The probe carries the block's own content, so a save makes it stale: without this the
   * editor would keep showing the text it opened with, and our own editor would go on
   * reporting unsaved changes after it had saved them.
   */
  const saved = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['editor-capability', blockKey] });
    onSaved();
  }, [queryClient, blockKey, onSaved]);

  const finish = useCallback(() => {
    saved();
    onClose();
  }, [saved, onClose]);

  useEffect(() => {
    if (!embedded) return;

    function onMessage(event: MessageEvent) {
      // Both embeds are served through our own origin (that is what lets the CMS cookie
      // ride along at all), so anything from another origin is not them. The frame check
      // then rules out any other iframe on the page posting a lookalike message.
      if (event.origin !== window.location.origin) return;
      if (!frameRef.current || event.source !== frameRef.current.contentWindow) return;

      const data: unknown = event.data;

      // ANY message from the frame — resize, save, cancel, an editor-done name — is proof
      // that its JS is running, which is the only thing the silence check is about. Set
      // before the resize branch below returns: `plugin.resize` is the message a healthy
      // v2 embed posts FIRST, so counting it last would count it never.
      setSpoke(true);

      if (isResizeMessage(data)) {
        const height = data.payload?.height;
        if (typeof height === 'number') {
          setFrameHeight(Math.min(Math.max(height, MIN_FRAME), MAX_FRAME));
        }
        return;
      }

      const blockEvent = xblockEvent(data);
      if (blockEvent) {
        // 'error' is deliberately not handled here: the embed shows its own message inside
        // the form, next to the field that caused it, and closing the dialog over the top
        // of that would throw away the author's work along with the explanation.
        if (blockEvent.eventName === 'save' && blockEvent.data?.state === 'end') finish();
        if (blockEvent.eventName === 'cancel') onClose();
        return;
      }

      if (isEditorDoneMessage(data)) finish();
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [embedded, finish, onClose]);

  /**
   * Re-read the frame after it has had time to start, and only if it has said nothing.
   *
   * Re-read rather than reuse the load-time probe: the globals appear when the scripts run,
   * so a snapshot taken at `load` would report every editor as runtime-less.
   */
  useEffect(() => {
    if (!loaded || spoke) {
      setSilent(false);
      return;
    }
    const timer = setTimeout(() => {
      setProbe(inspectEmbed(frameRef.current?.contentWindow ?? null, window.location.protocol));
      setSilent(true);
    }, EMBED_SILENCE_MS);
    return () => clearTimeout(timer);
  }, [loaded, spoke]);

  const fault = embedFault(probe, silent);

  const kind = capability.data?.kind;

  return (
    <Dialog
      title={`${readOnly ? 'View' : 'Edit'} ${blockName}`}
      wide
      onClose={onClose}
      footer={
        <>
          <Button variant="primary" onClick={onClose}>
            {readOnly ? 'Close' : 'Done editing'}
          </Button>
          {embedded && (
            <span className="compedit__note" data-fault={fault ?? undefined}>
              {fault
                ? // The old sentence sent the author to a Save that cannot work. Saying it
                  // anyway would be the same silence with extra steps.
                  'This editor cannot save — see the message above.'
                : 'Save inside the editor first. Closing here does not save.'}
            </span>
          )}
        </>
      }
    >
      {capability.isError ? (
        <Banner tone="danger">
          <strong>Could not open this component.</strong> The authoring service did not say
          how it should be edited. Close this and reload; nothing has been changed.
        </Banner>
      ) : !capability.data || !kind ? (
        <Spinner label="Opening the editor…" />
      ) : embedded ? (
        session.isError ? (
          <Banner tone="danger">
            <strong>Could not open the editor.</strong> Signing in to the authoring service
            failed, so the editor would only have shown a login page. Close this, reload the
            page and try again; if it keeps failing your account may not have authoring
            access to this content.
          </Banner>
        ) : !session.data ? (
          <Spinner label="Preparing the editor…" />
        ) : (
          <>
            {fault === 'assets' && (
              <Banner tone="danger">
                <strong>Nothing typed in this editor can be saved.</strong> The platform is
                asking for {probe?.blocked.length} of its own stylesheets and scripts over an
                insecure address ({blockedOrigins(probe?.blocked ?? []).join(', ')}) while
                this page is secure, so your browser blocked them and this editor&rsquo;s
                Save button has nothing behind it: it discards what you type without an
                error. Copy anything you need out of it, then close this. An operator has to
                correct the deployment&rsquo;s HTTPS setting — the fix is one setting and it
                is written up in <code>docs/authoring-embed-https.md</code>.
              </Banner>
            )}
            {fault === 'runtime' && (
              <Banner tone="danger">
                <strong>This editor has not started, so its Save will not work.</strong> The
                platform&rsquo;s own editor loaded its page but none of its scripts ran
                ({(probe?.missingRuntime ?? []).join(', ')} missing after{' '}
                {EMBED_SILENCE_MS / 1000} seconds), which leaves a form whose Save button
                discards what you type instead of sending it. Close this and reload; if it
                keeps happening, an operator needs to look at why the editor&rsquo;s assets
                are not loading.
              </Banner>
            )}
            <iframe
              ref={frameRef}
              className="compedit__frame"
              // Only set once the editor asks for a height. The stylesheet supplies a
              // sensible one until then, because Studio's own editor page does not post a
              // resize message at all on this release — only the v2 embed does.
              style={frameHeight ? { height: `${frameHeight}px` } : undefined}
              src={editorEmbedUrl(blockKey)}
              title={`Editing ${blockName}`}
              // Probed here rather than on a timer alone, because the blocked-asset check
              // is answerable the instant the document exists — the http:// URLs are in
              // the markup the platform sent.
              onLoad={() => {
                setProbe(
                  inspectEmbed(
                    frameRef.current?.contentWindow ?? null,
                    window.location.protocol,
                  ),
                );
                setLoaded(true);
              }}
            />
          </>
        )
      ) : capability.data.data === undefined ? (
        // Only reachable read-only: an editable block of a type with no `data` field always
        // has the platform's editor, which is what the branch above embeds.
        <Banner tone="info">
          <strong>You can see this component but not its source.</strong> The platform stores
          a <code>{capability.data.block_type}</code> component in settings fields rather
          than as text, so there is nothing to show here. Ask whoever administers this
          library for author access and its own editor opens instead.
        </Banner>
      ) : kind === 'question' && capability.data.question && capability.data.settings && !showSource ? (
        // A question the converter can represent COMPLETELY. One that it cannot never reaches
        // here — the server answers kind 'olx' with a refusal instead — so this form is never
        // holding a question it only half understands.
        <QuestionEditor
          question={capability.data.question}
          settings={capability.data.settings}
          showanswerChoices={capability.data.showanswer_choices ?? []}
          blockKey={blockKey}
          initialName={capability.data.display_name ?? ''}
          publishTarget={publishTarget}
          readOnly={readOnly}
          onSaved={saved}
          onEditSource={() => setShowSource(true)}
        />
      ) : kind === 'video' ? (
        // Video keeps its content in settings fields, not in `data`, so neither the
        // embedded editor nor the source one can show it — see VideoEditor.
        <VideoEditor
          blockKey={blockKey}
          initialFields={capability.data.fields ?? {}}
          initialName={capability.data.display_name ?? ''}
          readOnly={readOnly}
          onSaved={saved}
        />
      ) : (
        <>
          {/*
            WHY THE AUTHOR IS LOOKING AT SOURCE, in one sentence, and the three answers are
            genuinely different things. They asked for it; the form was refused and this says
            which construct stopped it; or the type simply has no editor. Saying the wrong one
            is worse than saying nothing — "this type has no editor" in front of an author who
            just clicked "Edit the XML instead" reads as a fault.
          */}
          {showSource && kind === 'question' ? (
            <p className="compedit__which">
              You are editing this question’s XML.{' '}
              <button
                type="button"
                className="qedit__link"
                onClick={() => setShowSource(false)}
              >
                Go back to the form
              </button>
            </p>
          ) : capability.data.question_refusal ? (
            <QuestionRefusalNote refusal={capability.data.question_refusal} />
          ) : (
            <p className="compedit__which">
              {readOnly
                ? 'Read only — you do not have author access to this library.'
                : 'This type has no editor of its own on this platform, so this is its source.'}
            </p>
          )}
          <DataEditor
            blockKey={blockKey}
            /*
              A question's source IS OLX, so it is handed over as such. Passing `'question'`
              through would cost the author the Tab-indents handler and the XML-specific
              explanation of a failed save — both of which are exactly what someone editing
              capa XML by hand needs, and both of which key off this prop.
            */
            kind={kind === 'question' ? 'olx' : kind}
            blockType={capability.data.block_type}
            initialData={capability.data.data}
            initialName={capability.data.display_name ?? ''}
            publishTarget={publishTarget}
            readOnly={readOnly}
            onSaved={saved}
          />
        </>
      )}
    </Dialog>
  );
}
